
export interface Specification {
    openapi: string
    info: {
        title: string
        description: string
        version: string
    }
    paths: { [path: string]: Path }
    components?: {
        schemas?: { [name: string]: ResolvedSchema }
        examples?: { [name: string]: ResolvedExample }
        "x-errorDefinitions"?: { [code: string]: XError }
    }
}

export interface Path {
    post?: Method
    get?: Method
}

export interface Method {
    summary: string
    description?: string
    security?: SecurityRequirement[]
    parameters?: Parameter[]
    requestBody?: Body
    responses?: {
        [statusCode: string]: Response
    }
    "x-errors"?: string[]
}

export type SecurityRequirement = {
    [securityScheme: string]: string[]
}

export interface Parameter {
    name: string
    description?: string
    in: "query" | "path" | "header" | "cookie"
    required?: boolean
    schema?: Schema
    explode?: boolean
    deprecated?: boolean
    example?: any
}

export interface Body {
    description?: string
    required?: boolean
    content?: {
        [contentType: string]: Content
    }
}

export interface Content {
    schema?: ObjectSchema
    example?: Example
}

export type Schema = ResolvedSchema | SchemaRef

export type ResolvedSchema = ObjectSchema | IntegerSchema | StringSchema

export interface BaseSchema<TName extends string, TValue> {
    type: TName
    description?: string
    default?: TValue
    nullable?: boolean
    example?: Example
}

export interface ObjectSchema extends BaseSchema<"object", any> {
    required?: string[]
    properties?: {
        [name: string]: Schema
    }
}

export interface IntegerSchema extends BaseSchema<"integer", number> {
    format?: IntegerFormat
    minimum?: number
    maximum?: number
}

export interface StringSchema extends BaseSchema<"string", string> {
    format?: StringFormat
    minLength?: number
    maxLength?: number
    enum?: string[]
}

export interface SchemaRef {
    $ref: string
}

export type StringFormat = "date-time" | "byte" | "binary" | string
export type NumberFormat = "float" | "double" | string
export type IntegerFormat = "int32" | "int64" | string
export type AllFormats = StringFormat | NumberFormat | IntegerFormat

export interface Response {
    description?: string
    content?: {
        [contentType: string]: Content
    }
}

export type Example = ResolvedExample | ExampleRef

export interface ResolvedExample {
    value?: any
}

export interface ExampleRef {
    $ref: string
}

export interface XError {
    description: string
}

export function isIntegerSchema(schema: ResolvedSchema): schema is IntegerSchema {
    return schema.type === "integer"
}

export function isStringSchema(schema: ResolvedSchema): schema is StringSchema {
    return schema.type === "string"
}

export function isSchemaRef(schema: Schema): schema is SchemaRef {
    const maybeRef = (schema as SchemaRef).$ref
    return maybeRef !== undefined && maybeRef.startsWith("#/components/schemas/")
}

export function hasLimits(schema: ResolvedSchema) {
    if (isIntegerSchema(schema)) {
        return schema.maximum !== undefined || schema.minimum !== undefined
    } else if (isStringSchema(schema)) {
        return schema.maxLength !== undefined || schema.minLength !== undefined
    } else {
        return false
    }
}

export function resolveSchema(schema: Schema, specification: Specification): ResolvedSchema
export function resolveSchema(schema: Schema | undefined, specification: Specification): undefined
export function resolveSchema(schema: Schema | undefined, specification: Specification): ResolvedSchema | undefined {
    if (!schema) {
        return schema
    }

    if (isSchemaRef(schema)) {
        const schemaName = schema.$ref.replace("#/components/schemas/", "")
        const resolvedSchema = specification.components?.schemas?.[schemaName]
        if (!resolvedSchema) {
            throw new Error(`Unable to resolve schema ref: ${schema.$ref}`)
        }
        return resolvedSchema
    } else {
        return schema
    }
}

export function resolveExample(example: Example, specification: Specification): ResolvedExample
export function resolveExample(example: Example | undefined, specification: Specification): undefined
export function resolveExample(example: Example | undefined, specification: Specification): ResolvedExample | undefined {
    if (!example) {
        return example
    }

    if (isExampleRef(example)) {
        const exampleName = example.$ref.replace("#/components/examples/", "")
        const resolvedExample = specification.components?.examples?.[exampleName]
        if (!resolvedExample) {
            throw new Error(`Unable to resolve example ref: ${example.$ref}`)
        }
        return resolvedExample
    } else {
        return example
    }
}

export function isExampleRef(example: Example): example is ExampleRef {
    return (example as ExampleRef).$ref !== undefined
}